<template>
  <div class="vehicle-detail-page">
    <div class="page-header">
      <span class="page-title">无人驾驶车辆(装备)详情</span>
      <a-button type="primary" @click="goBack">
        返回
        <template #icon><ArrowLeftOutlined /></template>
      </a-button>
    </div>

    <div class="page-body">
      <a-spin :spinning="loading">
        <div class="detail-form">
          <!-- 基本信息 -->
          <div class="form-section">
            <div class="section-title">基本信息</div>
            <a-row :gutter="24">
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">车辆编码</span>
                  <span class="detail-value">{{ detail.licenceNo || "-" }}</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">编码有效期</span>
                  <span class="detail-value">{{ licenceValidText }}</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">车辆识别代码</span>
                  <span class="detail-value">{{ detail.vin || "-" }}</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">车辆型号</span>
                  <span class="detail-value">{{ detail.model || "-" }}</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">类型</span>
                  <span class="detail-value">{{ typeName }}</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">生产企业</span>
                  <span class="detail-value">{{ detail.manufacturer || "-" }}</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">生产日期</span>
                  <span class="detail-value">{{ detail.productionDate || "-" }}</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">整机尺寸</span>
                  <span class="detail-value">{{ detail.overallSize || "-" }}</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">续航里程(km)</span>
                  <span class="detail-value">{{ detail.range ?? "-" }}</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">整备质量(kg)</span>
                  <span class="detail-value">{{ detail.curbWeight ?? "-" }}</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">载重(kg)</span>
                  <span class="detail-value">{{ detail.capacityLoad ?? "-" }}</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">最大速度(km/h)</span>
                  <span class="detail-value">{{ detail.maxSpeed ?? "-" }}</span>
                </div>
              </a-col>
            </a-row>
          </div>

          <!-- 车辆合格证 -->
          <div class="form-section">
            <div class="section-title">车辆合格证</div>
            <ImageUploader
              v-if="detail.certificateUrl"
              v-model:imageUrl="detail.certificateUrl"
              only-preview
              :box-width="320"
              :box-height="220"
            />
            <span v-else class="empty-tip">未上传</span>
          </div>

          <!-- 车辆保险信息 -->
          <div class="form-section">
            <div class="section-title">车辆保险信息</div>
            <a-row :gutter="24">
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">保险公司</span>
                  <span class="detail-value">{{ detail.insuranceCompany || "-" }}</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">保险单号</span>
                  <span class="detail-value">{{ detail.insuranceNo || "-" }}</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">保险金额</span>
                  <span class="detail-value">{{ detail.insuranceAmount ?? "-" }}</span>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="detail-item">
                  <span class="detail-label">保险起止日期</span>
                  <span class="detail-value">{{ insuranceDateText }}</span>
                </div>
              </a-col>
            </a-row>

            <div class="file-block">
              <span class="file-block-label">保险证明文件</span>
              <ul v-if="detail.insuranceFiles.length" class="file-list">
                <li
                  v-for="file in detail.insuranceFiles"
                  :key="file.fileUrl"
                  class="file-item"
                >
                  <FileOutlined class="file-icon" />
                  <a class="file-name" @click="handlePreview(file)">{{ file.fileName }}</a>
                </li>
              </ul>
              <span v-else class="empty-tip">暂无保险证明文件</span>
            </div>
          </div>

          <!-- 车辆测试报告 -->
          <div class="form-section">
            <div class="section-title">车辆测试报告</div>
            <div class="file-block">
              <span class="file-block-label">测试报告附件</span>
              <ul v-if="detail.testReportFiles.length" class="file-list">
                <li
                  v-for="file in detail.testReportFiles"
                  :key="file.fileUrl"
                  class="file-item"
                >
                  <FileOutlined class="file-icon" />
                  <a class="file-name" @click="handlePreview(file)">{{ file.fileName }}</a>
                </li>
              </ul>
              <span v-else class="empty-tip">暂无测试报告文件</span>
            </div>
          </div>
        </div>
      </a-spin>
    </div>

    <!-- 附件预览抽屉 -->
    <FilePreview
      v-model:visible="previewVisible"
      :file-url="previewFileUrl"
      :file-name="previewFileName"
      @close="previewVisible = false"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { message } from "ant-design-vue";
import { ArrowLeftOutlined, FileOutlined } from "@ant-design/icons-vue";
import ImageUploader from "@/components/imageUploader/index.vue";
import FilePreview from "@/components/FilePreview/index.vue";
import { getVehicleInfo } from "@/api/vehicle";
import { getVehicleTypeList } from "@/api/settings/vehicleType";

const route = useRoute();
const router = useRouter();

const loading = ref(false);
const vehicleId = ref(route.query.id || "");

// 车辆类型列表（用于回显类型名称）
const vehicleTypeOptions = ref([]);

// 车辆详情数据
const detail = reactive({
  licenceNo: "",
  licenceStartDate: "",
  licenceEndDate: "",
  vin: "",
  model: "",
  type: undefined,
  manufacturer: "",
  productionDate: undefined,
  overallSize: "",
  range: undefined,
  curbWeight: undefined,
  capacityLoad: undefined,
  maxSpeed: undefined,
  certificateUrl: "",
  insuranceCompany: "",
  insuranceNo: "",
  insuranceAmount: undefined,
  insuranceStartDate: "",
  insuranceEndDate: "",
  insuranceFiles: [],
  testReportFiles: [],
});

// 类型名称
const typeName = computed(() => {
  const target = vehicleTypeOptions.value.find((o) => o.id === detail.type);
  return target ? target.name : detail.type ?? "-";
});

// 保险起止日期展示
const insuranceDateText = computed(() => {
  if (detail.insuranceStartDate && detail.insuranceEndDate) {
    return `${detail.insuranceStartDate} ~ ${detail.insuranceEndDate}`;
  }
  return detail.insuranceStartDate || detail.insuranceEndDate || "-";
});

// 编码有效期展示
const licenceValidText = computed(() => {
  if (detail.licenceStartDate && detail.licenceEndDate) {
    return `${detail.licenceStartDate} ~ ${detail.licenceEndDate}`;
  }
  return detail.licenceStartDate || detail.licenceEndDate || "-";
});

// 文件预览
const previewVisible = ref(false);
const previewFileUrl = ref("");
const previewFileName = ref("");

const handlePreview = (file) => {
  previewFileUrl.value = file.fileUrl;
  previewFileName.value = file.fileName || "文件预览";
  previewVisible.value = true;
};

// 获取车辆类型列表
const loadVehicleTypeList = async () => {
  try {
    const res = await getVehicleTypeList({ categoryType: "autonomous_vehicle" });
    if (res.code === 200 && res.data) {
      vehicleTypeOptions.value = res.data;
    }
  } catch (error) {
    console.error("获取车辆类型列表失败:", error);
  }
};

// 加载车辆详情
const loadDetail = async () => {
  if (!vehicleId.value) return;
  loading.value = true;
  try {
    const res = await getVehicleInfo(vehicleId.value);
    if (res.code === 200 && res.data) {
      const d = res.data;
      // 基本信息
      detail.licenceNo = d.licenceNo || "";
      detail.licenceStartDate = d.licenceStartDate || "";
      detail.licenceEndDate = d.licenceEndDate || "";
      detail.vin = d.vin || "";
      detail.model = d.model || "";
      detail.type = d.type ?? undefined;
      detail.manufacturer = d.manufacturer || "";
      detail.productionDate = d.productionDate || undefined;
      detail.overallSize = d.overallSize || "";
      detail.range = d.range ?? undefined;
      detail.curbWeight = d.curbWeight ?? undefined;
      detail.capacityLoad = d.capacityLoad ?? undefined;
      detail.maxSpeed = d.maxSpeed ?? undefined;
      // 车辆合格证图片
      detail.certificateUrl = d.certificateUrl || "";
      // 车辆保险信息
      detail.insuranceCompany = d.insuranceCompany || "";
      detail.insuranceNo = d.insuranceNo || "";
      detail.insuranceAmount = d.insuranceAmount ?? undefined;
      detail.insuranceStartDate = d.insuranceStartDate || "";
      detail.insuranceEndDate = d.insuranceEndDate || "";
      // 附件列表
      detail.insuranceFiles = Array.isArray(d.insuranceFiles)
        ? d.insuranceFiles.map((f) => ({ fileName: f.fileName, fileUrl: f.fileUrl }))
        : [];
      detail.testReportFiles = Array.isArray(d.testReportFiles)
        ? d.testReportFiles.map((f) => ({ fileName: f.fileName, fileUrl: f.fileUrl }))
        : [];
    } else {
      message.error(res.msg || "获取车辆详情失败");
    }
  } catch (error) {
    console.error("获取车辆详情失败:", error);
    message.error("获取车辆详情失败");
  } finally {
    loading.value = false;
  }
};

const goBack = () => {
  router.back();
};

onMounted(async () => {
  await loadVehicleTypeList();
  await loadDetail();
});
</script>

<style scoped lang="less">
.vehicle-detail-page {
  display: flex;
  flex-direction: column;
  padding: 16px;
  height: calc(100vh - 70px);
  background-color: #ffffff;
  overflow: hidden;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 16px;
  flex-shrink: 0;

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: #262626;
  }
}

.page-body {
  flex: 1;
  overflow-y: auto;
}

.detail-form {
  max-width: 1000px;
  margin: 0 auto;
}

.form-section {
  margin-bottom: 32px;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 16px;
    padding-left: 10px;
    border-left: 3px solid #1890ff;
    line-height: 1;
  }
}

.detail-item {
  display: flex;
  margin-bottom: 16px;

  .detail-label {
    width: 110px;
    flex-shrink: 0;
    color: #8c8c8c;
  }

  .detail-value {
    color: #262626;
    word-break: break-all;
  }
}

.file-block {
  margin-top: 8px;

  .file-block-label {
    display: block;
    margin-bottom: 8px;
    color: #8c8c8c;
  }
}

.file-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.file-item {
  display: flex;
  align-items: center;
  padding: 6px 0;

  .file-icon {
    color: #1890ff;
    margin-right: 8px;
  }

  .file-name {
    color: #1e8ef2;
  }
}

.empty-tip {
  color: #999;
  font-size: 13px;
}
</style>
